import { FulfillmentStatus, FulfillmentType } from '@/api/common-types'
import { useGetOrder, useGetOrderReceiptUrl } from '@/api/react-query'
import { StoreOrderItem } from '@/api/store-orders-types'
import { ErrorDisplay } from '@/components/error-display'
import { LoadingSpinner } from '@/components/loading-spinner'
import { PickupCodeComponent } from '@/components/pickup-code'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Image } from '@/components/ui/image'
import { useI18n } from '@/i18n/i18n-react'
import { tenant } from '@/tenants/tenant'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Download, QrCode } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/receipt/$receiptId')({
	component: Receipt
})

function Receipt() {
	const { receiptId } = Route.useParams()
	const { L } = useI18n()
	const order = useGetOrder({ orderUuid: receiptId })
	const pdfUrl = useGetOrderReceiptUrl({ orderUuid: receiptId }, { format: 'pdf' })
	const [pickupCodeModalOpen, setPickupCodeModalOpen] = useState(false)

	if (order.isLoading) {
		return (
			<div className='flex items-center justify-center py-12'>
				<LoadingSpinner />
			</div>
		)
	}

	if (order.error) {
		return (
			<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
				<Card className='p-6 sm:p-8 lg:p-12'>
					<ErrorDisplay refetch={order.refetch} className='space-y-4' />
				</Card>
			</div>
		)
	}

	if (!order.data) {
		return (
			<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
				<Card className='p-6 sm:p-8 lg:p-12'>
					<div className='space-y-4 text-center'>
						<p className='text-muted-foreground text-lg sm:text-xl'>{L.Receipt.NotFound()}</p>
						<Button asChild variant='outline'>
							<Link to='/'>
								<ArrowLeft className='mr-2 h-4 w-4' />
								{L.Receipt.BackToReceipts()}
							</Link>
						</Button>
					</div>
				</Card>
			</div>
		)
	}

	const enablePickupModal =
		order.data.fulfillmentType === FulfillmentType.Ecom &&
		(order.data.fulfillmentStatus === FulfillmentStatus.NotFulfilled ||
			order.data.fulfillmentStatus === FulfillmentStatus.PartiallyFulfilled)
	const items = order.data.itemGroups?.flatMap(group => group.items) ?? []

	return (
		<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
			<Helmet>
				<title>{L.Receipt.TitleWithOrderNumber(`#${order.data.displayId}`)}</title>
			</Helmet>
			<div className='flex flex-col gap-4 pb-6 sm:flex-row sm:items-center sm:justify-between sm:pb-8'>
				<h1 className='text-2xl font-bold sm:text-3xl lg:text-4xl'>{L.Receipt.Title()}</h1>
				<div className='text-left sm:text-right'>
					<p className='text-muted-foreground text-sm'>{L.Receipt.OrderId()}</p>
					<p className='font-mono text-sm sm:text-base'>
						{L.Receipt.OrderNumber(order.data.displayId?.toString() ?? receiptId)}
					</p>
				</div>
			</div>

			<Card className='mb-6 sm:mb-8'>
				<div className='space-y-4 p-4 sm:space-y-6 sm:px-6'>
					<div className='space-y-3 sm:space-y-4'>
						{items.map((item: StoreOrderItem) => (
							<Link key={item.uuid} to='/product/$productId' params={{ productId: item.variantUuid }}>
								<div className='border-border/40 flex flex-col gap-3 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:py-4'>
									<div className='mx-auto shrink-0 rounded-lg bg-gray-50 p-2 sm:mx-0'>
										<Image
											src={item.variantImageUrl ?? undefined}
											alt={item.productTitle}
											className='aspect-square h-16 w-16 object-contain sm:h-20 sm:w-20'
										/>
									</div>
									<div className='flex-grow text-center sm:px-4 sm:text-left lg:px-6'>
										<h3 className='text-sm font-medium sm:text-base'>{item.productTitle}</h3>
										{item.variantTitle && (
											<p className='text-muted-foreground text-xs sm:text-sm'>
												{item.variantTitle}
											</p>
										)}
										{item.unitPrice && (
											<p className='text-muted-foreground text-xs sm:text-sm'>
												{L.Amount.QuantityTimesPrice(item.quantityInfo.quantity, {
													amount: item.unitPrice.gross,
													currencyCode: order.data.currencyCode
												})}
											</p>
										)}
									</div>
									<div className='text-center sm:text-right'>
										<p className='text-sm font-medium sm:text-base'>
											{L.Amount.Default({
												amount: item.totals.gross,
												currencyCode: order.data.currencyCode
											})}
										</p>
									</div>
								</div>
							</Link>
						))}
					</div>
				</div>
			</Card>

			<Card>
				<div className='bg-secondary/5 p-4 sm:p-6'>
					<div className='space-y-2'>
						<div className='flex justify-between font-medium'>
							<p className='text-sm sm:text-base'>{L.Receipt.Total()}</p>
							<p className='text-lg font-bold sm:text-xl'>
								{L.Amount.Default({
									amount: order.data.totals.gross,
									currencyCode: order.data.currencyCode
								})}
							</p>
						</div>
					</div>
				</div>
			</Card>

			<div className='my-6 flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4'>
				<Button asChild className='w-full sm:w-auto'>
					<Link to='/receipts'>
						<ArrowLeft className='mr-2 h-4 w-4' />
						{L.Receipt.BackToReceipts()}
					</Link>
				</Button>
				<Button
					variant='outline'
					className='w-full sm:w-auto'
					onClick={() => window.open(pdfUrl.data?.url, '_blank')}
					isLoading={pdfUrl.isLoading}
					disabled={!pdfUrl.data?.url}
				>
					<Download className='mr-2 h-4 w-4' />
					{L.Receipt.DownloadPDF()}
				</Button>
				{enablePickupModal && (
					<Button variant='outline' className='w-full sm:w-auto' onClick={() => setPickupCodeModalOpen(true)}>
						<QrCode className='mr-2 h-4 w-4' />
						{L.Receipt.ShowPickupCode()}
					</Button>
				)}
			</div>
			{enablePickupModal && (
				<PickupCodeComponent
					title={L.Receipt.PickupCode()}
					isOpen={pickupCodeModalOpen}
					onClose={() => setPickupCodeModalOpen(false)}
					orderId={order.data.uuid}
					tenant={tenant.features.name}
				/>
			)}
		</div>
	)
}

export default Receipt
